import axios from 'axios';

// Smart backend URL detection.
// - When app is served from the SAME domain as the backend (Emergent production
//   deploy where the ingress proxies /api → backend), use SAME-ORIGIN.
//   This avoids CORS issues + ISP/firewall blocks on the sandbox preview domain
//   that broke access for some users (some iPhones could open, others couldn't).
// - When running locally / in dev preview, fall back to REACT_APP_BACKEND_URL.
function resolveBackendBase() {
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (typeof window === 'undefined') return envUrl; // SSR safety, unused
  const host = window.location.hostname;
  // Treat as dev/preview if host matches localhost or emergentagent preview subdomain.
  const isDevHost =
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.endsWith('.preview.emergentagent.com') ||
    host.endsWith('.emergentagent.com');
  if (isDevHost) return envUrl;
  // Production custom domain → use same origin so /api hits the deploy's ingress.
  return window.location.origin;
}

const BACKEND_URL = resolveBackendBase();
// Expose globally so non-axios consumers (Audio, fetch, WebSocket) can share the same base.
if (typeof window !== 'undefined') {
  window.__hafizim_backend_url = BACKEND_URL;
}
export { BACKEND_URL };
export const API = `${BACKEND_URL}/api`;

let authToken = localStorage.getItem('hafizim_token');

export const setAuthToken = (token) => {
  authToken = token;
  if (token) {
    localStorage.setItem('hafizim_token', token);
  } else {
    localStorage.removeItem('hafizim_token');
  }
};

export const getAuthToken = () => {
  return authToken || localStorage.getItem('hafizim_token');
};

const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const authAPI = {
  register: (data) => apiClient.post('/auth/register', data),
  login: (data) => apiClient.post('/auth/login', data),
  getMe: () => apiClient.get('/auth/me'),
  verifyEmail: (email, code) => apiClient.post('/auth/verify-email', { email, code }),
  resendVerification: (email) => apiClient.post('/auth/resend-verification', { email }),
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (email, code, new_password) =>
    apiClient.post('/auth/reset-password', { email, code, new_password }),
};

export const quranAPI = {
  // Legacy (old backend routes)
  getSurahs: () => apiClient.get('/quran/surahs'),
  getAyahs: (surahNumber) => apiClient.get(`/quran/surahs/${surahNumber}/ayahs`),
  getAyah: (surahNumber, ayahNumber) => apiClient.get(`/quran/ayah/${surahNumber}/${ayahNumber}`),
  getOnlineSurah: (surahNumber) => apiClient.get(`/quran/online/surah/${surahNumber}`),
  // New (v2 endpoints with edition + reciters)
  getSurahList: () => apiClient.get('/quran/v2/surah-list'),
  getSurah: (number, edition = 'quran-uthmani') => apiClient.get(`/quran/v2/surah/${number}?edition=${edition}`),
  getPage: (pageNumber, edition = 'quran-uthmani', withMeal = true, mealEdition) => {
    const params = new URLSearchParams({ edition, with_meal: String(withMeal) });
    if (mealEdition) params.set('meal_edition', mealEdition);
    return apiClient.get(`/quran/v2/page/${pageNumber}?${params.toString()}`);
  },
  getSurahPages: (surahNumber) => apiClient.get(`/quran/v2/surah-pages/${surahNumber}`),
  getReciters: () => apiClient.get('/quran/v2/reciters'),
  mealEditions: () => apiClient.get('/quran/v2/meal-editions'),
  getAyahAudioUrl: (reciter, surah, ayah) => `${BACKEND_URL}/api/quran/v2/audio/${reciter}/${surah}/${ayah}`,
  getSurahAudioUrl: (reciter, surah) => `${BACKEND_URL}/api/quran/v2/surah-audio/${reciter}/${surah}`,
};

export const realtimeAPI = {
  startTracking: (data) => apiClient.post('/realtime/start-tracking', data),
  processWord: (data) => apiClient.post('/realtime/process-word', data),
  getSessionSummary: (sessionId) => apiClient.get(`/realtime/session/${sessionId}/summary`),
  resetSession: (sessionId) => apiClient.post(`/realtime/session/${sessionId}/reset`),
  endSession: (sessionId) => apiClient.delete(`/realtime/session/${sessionId}`),
};

export const speechAPI = {
  transcribe: (formData) => apiClient.post('/speech/transcribe', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  textToSpeech: (text, language = 'ar') => apiClient.post('/speech/tts', { text, language }),
};

export const progressAPI = {
  getUserProgress: () => apiClient.get('/progress/user'),
  updateProgress: (data) => apiClient.post('/progress/update', data),
  getStats: () => apiClient.get('/progress/stats'),
};

export const badgeAPI = {
  getAllBadges: () => apiClient.get('/badges'),
  getUserBadges: () => apiClient.get('/badges/user'),
  checkBadges: () => apiClient.post('/badges/check'),
  markBadgeSeen: (badgeId) => apiClient.post(`/badges/${badgeId}/mark-seen`),
};

export const challengesAPI = {
  create: (name, target_surahs = 114) => apiClient.post('/hatim-challenges/create', { name, target_surahs }),
  join: (code) => apiClient.post(`/hatim-challenges/join/${code}`),
  my: () => apiClient.get('/hatim-challenges/my'),
  get: (id) => apiClient.get(`/hatim-challenges/${id}`),
  contribute: (id, surah) => apiClient.post(`/hatim-challenges/${id}/contribute`, { surah }),
};

export const socialAPI = {
  searchUsers: (query) => apiClient.get(`/users/search?query=${encodeURIComponent(query)}`),
  sendFriendRequest: (userId) => apiClient.post(`/friends/request/${userId}`),
  getFriendRequests: () => apiClient.get('/friends/requests'),
  acceptFriendRequest: (requestId) => apiClient.post(`/friends/accept/${requestId}`),
  rejectFriendRequest: (requestId) => apiClient.delete(`/friends/reject/${requestId}`),
  getFriends: () => apiClient.get('/friends'),
  getLeaderboard: (period = 'week') => apiClient.get(`/leaderboard?period=${period}`),
};

export const chatAPI = {
  history: (friendId) => apiClient.get(`/chat/${friendId}/history`),
  onlineFriends: () => apiClient.get('/chat/friends/online'),
  onlineCount: () => apiClient.get('/chat/admin/online-count'),
  react: (messageId, emoji) => apiClient.post(`/chat/messages/${messageId}/react`, { emoji }),
};

export const dailyVerseAPI = {
  today: () => apiClient.get('/daily-verse'),
};

export const pushAPI = {
  getPublicKey: () => apiClient.get('/push/vapid-public-key'),
  subscribe: (subscription) => apiClient.post('/push/subscribe', { subscription }),
  unsubscribe: (endpoint) => apiClient.post('/push/unsubscribe', { endpoint }),
  test: () => apiClient.post('/push/test', {}),
};

export const uploadsAPI = {
  chatImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post('/uploads/chat-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const quranV2 = {
  mealEditions: () => apiClient.get('/quran/v2/meal-editions'),
};

export const settingsAPI = {
  updateSettings: (data) => apiClient.put('/user/settings', null, { params: data }),
  addFavorite: (surahNumber) => apiClient.post(`/user/favorites/${surahNumber}`),
  removeFavorite: (surahNumber) => apiClient.delete(`/user/favorites/${surahNumber}`),
  getFavorites: () => apiClient.get('/user/favorites'),
};

export const playlistAPI = {
  createPlaylist: (name, surahIds, difficulty) => apiClient.post('/playlists', { name, surah_ids: surahIds, difficulty }),
  getPlaylists: () => apiClient.get('/playlists'),
  deletePlaylist: (playlistId) => apiClient.delete(`/playlists/${playlistId}`),
};

export const streakAPI = {
  getStreak: () => apiClient.get('/streak'),
  updateStreak: () => apiClient.post('/streak/update'),
};

export const challengeAPI = {
  createChallenge: (friendId, surahNumber, durationDays) => 
    apiClient.post('/challenges/create', { friend_id: friendId, surah_number: surahNumber, duration_days: durationDays }),
  getChallenges: () => apiClient.get('/challenges'),
  updateScore: (challengeId, score) => 
    apiClient.post(`/challenges/${challengeId}/update-score`, { score }),
};

export const offlineAPI = {
  cacheSurah: (surahNumber) => apiClient.post(`/offline/cache-surah/${surahNumber}`),
  getCachedSurahs: () => apiClient.get('/offline/cached-surahs'),
  getCachedSurah: (surahNumber) => apiClient.get(`/offline/surah/${surahNumber}`),
};

export const hatimAPI = {
  getProgress: () => apiClient.get('/hatim/progress'),
  savePosition: (surah, ayah) => apiClient.post('/hatim/save-position', { surah, ayah }),
  completeSurah: (surah) => apiClient.post('/hatim/complete-surah', { surah }),
  reset: () => apiClient.post('/hatim/reset'),
};

export const elifbaAPI = {
  getLetters: () => apiClient.get('/elifba/letters'),
  getProgress: () => apiClient.get('/elifba/progress'),
  markCompleted: (letter_id) => apiClient.post(`/elifba/complete-letter/${letter_id}`),
  getHarekeler: () => apiClient.get('/elifba/curriculum/harekeler'),
  getKelimeler: () => apiClient.get('/elifba/curriculum/kelimeler'),
  getKisaSureler: () => apiClient.get('/elifba/curriculum/kisa-sureler'),
};

export const hafizlikAPI = {
  juzMeta: () => apiClient.get('/hafizlik/juz-meta'),
  cig: (juz, count = 3) => apiClient.get(`/hafizlik/cig/${juz}?count=${count}`),
  listPageProgress: () => apiClient.get('/hafizlik/page-progress'),
  upsertPageProgress: (page, status, mode = 'learning') =>
    apiClient.post('/hafizlik/page-progress', { page, status, mode }),
  saveLastPosition: (page, mode = 'learning') =>
    apiClient.post('/hafizlik/last-position', { page, mode }),
  getLastPosition: () => apiClient.get('/hafizlik/last-position'),
};

export const accountAPI = {
  exportData: () => apiClient.get('/account/export-data', { responseType: 'blob' }),
  deleteAccount: () => apiClient.delete('/account/delete'),
};

export const consentsAPI = {
  get: () => apiClient.get('/user/consents'),
  update: (payload) => apiClient.put('/user/consents', payload),
};

export const supportAPI = {
  submit: (payload) => apiClient.post('/support/submit', payload),
  // admin
  list: (status, category) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (category) params.append('category', category);
    return apiClient.get(`/admin/support/?${params.toString()}`);
  },
  update: (id, body) => apiClient.patch(`/admin/support/${id}`, body),
};

export default apiClient;
